import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { useStores } from '../contexts/StoreContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const Cashea = () => {
  const [activeTab, setActiveTab] = useState('register');
  const { stores } = useStores();
  const [sales, setSales] = useState([]);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Form data for new sale
  const [formData, setFormData] = useState({
    date: new Date(),
    store_id: '',
    client_cedula: '',
    client_name: '',
    total_amount_usd: '',
    initial_payment_usd: ''
  });

  // Payment form data
  const [paymentData, setPaymentData] = useState({
    payment_date: new Date(),
    payment_amount_ves: '',
    payment_exchange_rate: ''
  });

  const [selectedSale, setSelectedSale] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    store_id: '',
    start_date: null,
    end_date: null
  });

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingSales();
    } else if (activeTab === 'paid') {
      fetchPaidSales();
    } else if (activeTab === 'dashboard') {
      fetchDashboardMetrics();
    }
  }, [activeTab]);

  const fetchPendingSales = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ status: 'pending' });
      if (filters.store_id) params.append('store_id', filters.store_id);
      if (filters.start_date) params.append('start_date', filters.start_date.toISOString().split('T')[0]);
      if (filters.end_date) params.append('end_date', filters.end_date.toISOString().split('T')[0]);
      
      const response = await apiClient.get(`/cashea-sales?${params}`);
      setSales(response.data);
    } catch (error) {
      console.error('Error fetching pending sales:', error);
      alert('Error al cargar las ventas pendientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaidSales = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ status: 'paid' });
      if (filters.store_id) params.append('store_id', filters.store_id);
      if (filters.start_date) params.append('start_date', filters.start_date.toISOString().split('T')[0]);
      if (filters.end_date) params.append('end_date', filters.end_date.toISOString().split('T')[0]);
      
      const response = await apiClient.get(`/cashea-sales?${params}`);
      setSales(response.data);
    } catch (error) {
      console.error('Error fetching paid sales:', error);
      alert('Error al cargar las ventas pagadas');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardMetrics = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/cashea-sales/dashboard/metrics');
      setDashboardMetrics(response.data);
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      alert('Error al cargar métricas del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!formData.store_id || !formData.client_cedula || !formData.client_name) {
      alert('Por favor complete todos los campos requeridos');
      return;
    }

    const totalAmount = parseFloat(formData.total_amount_usd);
    const initialPayment = parseFloat(formData.initial_payment_usd);

    if (isNaN(totalAmount) || totalAmount <= 0) {
      alert('El monto total debe ser mayor a 0');
      return;
    }

    if (isNaN(initialPayment) || initialPayment < 0) {
      alert('La inicial debe ser un valor válido');
      return;
    }

    if (initialPayment >= totalAmount) {
      alert('La inicial debe ser menor al monto total');
      return;
    }

    try {
      setLoading(true);
      
      const saleData = {
        ...formData,
        date: formData.date.toISOString().split('T')[0],
        total_amount_usd: totalAmount,
        initial_payment_usd: initialPayment
      };

      await apiClient.post('/cashea-sales', saleData);
      
      alert('Venta Cashea registrada exitosamente');
      
      // Reset form
      setFormData({
        date: new Date(),
        store_id: '',
        client_cedula: '',
        client_name: '',
        total_amount_usd: '',
        initial_payment_usd: ''
      });
    } catch (error) {
      console.error('Error creating Cashea sale:', error);
      alert('Error al registrar la venta Cashea: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = (sale) => {
    setSelectedSale(sale);
    setPaymentData({
      payment_date: new Date(),
      payment_amount_ves: '',
      payment_exchange_rate: ''
    });
    setShowPaymentModal(true);
  };

  const submitPayment = async () => {
    if (!paymentData.payment_amount_ves || !paymentData.payment_exchange_rate) {
      alert('Por favor complete todos los campos del pago');
      return;
    }

    try {
      setLoading(true);
      
      const payment = {
        payment_date: paymentData.payment_date.toISOString().split('T')[0],
        payment_amount_ves: parseFloat(paymentData.payment_amount_ves),
        payment_exchange_rate: parseFloat(paymentData.payment_exchange_rate)
      };

      await apiClient.put(`/cashea-sales/${selectedSale.id}`, payment);
      
      alert('Venta marcada como pagada exitosamente');
      setShowPaymentModal(false);
      setSelectedSale(null);
      
      // Refresh data
      if (activeTab === 'pending') {
        fetchPendingSales();
      } else if (activeTab === 'dashboard') {
        fetchDashboardMetrics();
      }
    } catch (error) {
      console.error('Error marking sale as paid:', error);
      alert('Error al marcar la venta como pagada: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('¿Está seguro que desea eliminar esta venta Cashea?')) {
      return;
    }

    try {
      setLoading(true);
      await apiClient.delete(`/cashea-sales/${saleId}`);
      alert('Venta eliminada exitosamente');
      fetchPendingSales();
    } catch (error) {
      console.error('Error deleting sale:', error);
      alert('Error al eliminar la venta: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : storeId;
  };

  const calculateFinancedAmount = () => {
    const total = parseFloat(formData.total_amount_usd) || 0;
    const initial = parseFloat(formData.initial_payment_usd) || 0;
    return total - initial;
  };

  const calculateCommission = () => {
    return calculateFinancedAmount() * 0.03;
  };

  const calculateNetAmount = () => {
    return calculateFinancedAmount() - calculateCommission();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">💳 Cashea</h1>
        <p className="text-gray-600">Gestión de ventas financiadas con Cashea</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('register')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'register'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📝 Registrar Venta
          </button>
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ⏳ Ventas Pendientes
          </button>
          <button
            onClick={() => setActiveTab('paid')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'paid'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            ✅ Ventas Pagadas
          </button>
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📊 Dashboard
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'register' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-6">Registrar Nueva Venta Cashea</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Fecha */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Venta *
                </label>
                <DatePicker
                  selected={formData.date}
                  onChange={(date) => setFormData({ ...formData, date })}
                  dateFormat="dd/MM/yyyy"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Tienda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tienda *
                </label>
                <select
                  value={formData.store_id}
                  onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Seleccione una tienda</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              </div>

              {/* Cédula */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cédula del Cliente *
                </label>
                <input
                  type="text"
                  value={formData.client_cedula}
                  onChange={(e) => setFormData({ ...formData, client_cedula: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="V-12345678"
                  required
                />
              </div>

              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nombre completo"
                  required
                />
              </div>

              {/* Monto Total */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Monto Total (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.total_amount_usd}
                  onChange={(e) => setFormData({ ...formData, total_amount_usd: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Inicial */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Inicial Pagada (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.initial_payment_usd}
                  onChange={(e) => setFormData({ ...formData, initial_payment_usd: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            {/* Calculated Values */}
            {formData.total_amount_usd && formData.initial_payment_usd && (
              <div className="bg-blue-50 p-4 rounded-lg space-y-2">
                <h3 className="font-semibold text-blue-900 mb-3">Cálculos Automáticos:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Monto Financiado:</p>
                    <p className="text-lg font-bold text-blue-900">{formatCurrency(calculateFinancedAmount())}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Comisión Cashea (3%):</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(calculateCommission())}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Neto a Recibir:</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(calculateNetAmount())}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setFormData({
                  date: new Date(),
                  store_id: '',
                  client_cedula: '',
                  client_name: '',
                  total_amount_usd: '',
                  initial_payment_usd: ''
                })}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Limpiar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Guardando...' : 'Registrar Venta'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Pending Sales Tab */}
      {activeTab === 'pending' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Ventas Pendientes de Pago</h2>
            <button
              onClick={fetchPendingSales}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              🔄 Actualizar
            </button>
          </div>

          {/* Filters */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tienda</label>
              <select
                value={filters.store_id}
                onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Todas las tiendas</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <p className="text-center py-8 text-gray-500">Cargando...</p>
          ) : sales.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No hay ventas pendientes</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tienda</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cédula</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Inicial</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Neto a Recibir</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Esperada</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sales.map(sale => (
                    <tr key={sale.id}>
                      <td className="px-4 py-3 text-sm">{formatDate(sale.date)}</td>
                      <td className="px-4 py-3 text-sm">{getStoreName(sale.store_id)}</td>
                      <td className="px-4 py-3 text-sm">{sale.client_name}</td>
                      <td className="px-4 py-3 text-sm">{sale.client_cedula}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(sale.total_amount_usd)}</td>
                      <td className="px-4 py-3 text-sm text-right">{formatCurrency(sale.initial_payment_usd)}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                        {formatCurrency(sale.net_amount_to_receive_usd)}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(sale.expected_payment_date)}</td>
                      <td className="px-4 py-3 text-sm text-center space-x-2">
                        <button
                          onClick={() => handleMarkAsPaid(sale)}
                          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                        >
                          💰 Marcar Pagado
                        </button>
                        <button
                          onClick={() => handleDelete(sale.id)}
                          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                        >
                          🗑️ Eliminar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Paid Sales Tab */}
      {activeTab === 'paid' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Historial de Ventas Pagadas</h2>
            <button
              onClick={fetchPaidSales}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              🔄 Actualizar
            </button>
          </div>

          {loading ? (
            <p className="text-center py-8 text-gray-500">Cargando...</p>
          ) : sales.length === 0 ? (
            <p className="text-center py-8 text-gray-500">No hay ventas pagadas</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Venta</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Neto Recibido</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Pago</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto VES</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tasa</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sales.map(sale => (
                    <tr key={sale.id}>
                      <td className="px-4 py-3 text-sm">{formatDate(sale.date)}</td>
                      <td className="px-4 py-3 text-sm">{sale.client_name}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                        {formatCurrency(sale.net_amount_to_receive_usd)}
                      </td>
                      <td className="px-4 py-3 text-sm">{formatDate(sale.payment_date)}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        Bs. {new Intl.NumberFormat('es-VE').format(sale.payment_amount_ves || 0)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {sale.payment_exchange_rate?.toFixed(2) || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboardMetrics && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">💰 Total Pendiente</h3>
              <p className="text-3xl font-bold text-orange-600">
                {formatCurrency(dashboardMetrics.total_pending_usd)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {dashboardMetrics.pending_sales_count} ventas
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">✅ Total Pagado</h3>
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(dashboardMetrics.total_paid_usd)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {dashboardMetrics.paid_sales_count} ventas
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">📊 Comisión Total</h3>
              <p className="text-3xl font-bold text-red-600">
                {formatCurrency(dashboardMetrics.total_commission_usd)}
              </p>
              <p className="text-sm text-gray-500 mt-1">3% sobre financiado</p>
            </div>
          </div>

          {/* Upcoming Payments */}
          {dashboardMetrics.upcoming_payments.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">⏰ Próximos Pagos (7 días)</h3>
              <div className="space-y-3">
                {dashboardMetrics.upcoming_payments.map(payment => (
                  <div key={payment.id} className="flex justify-between items-center p-3 bg-yellow-50 rounded">
                    <div>
                      <p className="font-medium">{payment.client_name}</p>
                      <p className="text-sm text-gray-600">{getStoreName(payment.store_id)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{formatCurrency(payment.amount_usd)}</p>
                      <p className="text-sm text-gray-600">{formatDate(payment.expected_date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">💰 Registrar Pago de Cashea</h3>
            
            <div className="mb-4 p-3 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">Cliente: <span className="font-medium">{selectedSale.client_name}</span></p>
              <p className="text-sm text-gray-600">Monto a recibir: <span className="font-bold text-green-600">{formatCurrency(selectedSale.net_amount_to_receive_usd)}</span></p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Pago</label>
                <DatePicker
                  selected={paymentData.payment_date}
                  onChange={(date) => setPaymentData({ ...paymentData, payment_date: date })}
                  dateFormat="dd/MM/yyyy"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Monto Pagado (VES)</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentData.payment_amount_ves}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_amount_ves: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tasa de Cambio</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentData.payment_exchange_rate}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_exchange_rate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="0.00"
                />
              </div>

              {paymentData.payment_amount_ves && paymentData.payment_exchange_rate && (
                <div className="p-3 bg-blue-50 rounded">
                  <p className="text-sm text-gray-600">Equivalente USD:</p>
                  <p className="text-xl font-bold text-blue-900">
                    {formatCurrency(parseFloat(paymentData.payment_amount_ves) / parseFloat(paymentData.payment_exchange_rate))}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedSale(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={submitPayment}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400"
              >
                {loading ? 'Procesando...' : 'Confirmar Pago'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cashea;
